import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import StaffManagement from "@/components/StaffManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart3,
  Users,
  Car,
  FileText,
  Shield,
  Calendar,
} from "lucide-react";
import VehicleRegistrationTable from "@/components/VehicleRegistrationTable";
import React, { useEffect, useState } from "react";

interface AdminDashboardProps {
  userRole: "staff" | "admin";
  userName: string;
  userId: number;
}

interface DashboardStats {
  totalRegistrations: number;
  activeStaff: number;
  pendingReviews: number;
  thisMonth: number;
  changes?: {
    totalRegistrations?: string | number;
    activeStaff?: string | number;
    pendingReviews?: string | number;
    thisMonth?: string | number;
    [key: string]: string | number | undefined;
  };
}

const AdminDashboard = ({
  userRole,
  userName,
  userId,
}: AdminDashboardProps) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const statsConfig = [
    {
      key: "totalRegistrations",
      title: "Total Registrations",
      icon: Car,
      color: "text-blue-600",
    },
    {
      key: "activeStaff",
      title: "Active Staff",
      icon: Users,
      color: "text-green-600",
    },
    {
      key: "pendingReviews",
      title: "Pending Reviews",
      icon: FileText,
      color: "text-orange-600",
    },
    {
      key: "thisMonth",
      title: "This Month",
      icon: BarChart3,
      color: "text-purple-600",
    },
  ];

  useEffect(() => {
    setLoading(true);
    // Use VITE_API_URL if available, else fallback to relative path
    const apiUrl =
      import.meta.env && import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/stats/dashboard`
        : "/api/stats/dashboard";
    fetch(apiUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center shadow-md">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">
                Manage registrations and staff accounts
              </p>
            </div>
          </div>
          <Badge variant="outline" className="px-3 py-1">
            <Calendar className="w-4 h-4 mr-1" />
            {new Date().toLocaleDateString("en-GB")}
          </Badge>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {loading
            ? statsConfig.map((stat, idx) => (
                <Card key={idx} className="shadow-lg">
                  <CardContent className="p-6 flex items-center justify-center min-h-[120px]">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </CardContent>
                </Card>
              ))
            : statsConfig.map((stat, idx) => (
                <Card
                  key={idx}
                  className="shadow-lg hover:shadow-xl transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {stat.title}
                        </p>
                        <p className="text-3xl font-bold">
                          {stats ? stats[stat.key] : "-"}
                        </p>
                        <p className="text-sm text-success">
                          {stats && stats.changes
                            ? stats.changes[stat.key]
                            : "-"}
                        </p>
                      </div>
                      <stat.icon className={`w-12 h-12 ${stat.color}`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Main Content */}
        <Tabs defaultValue="table" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="table">Vehicle Registrations</TabsTrigger>
            <TabsTrigger value="users">Staff Management</TabsTrigger>
          </TabsList>

          {/* Vehicle Registrations Tab */}
          <TabsContent value="table">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Vehicle Registrations</CardTitle>
                <CardDescription>
                  View, manage, and export vehicle registration records
                </CardDescription>
              </CardHeader>
              <CardContent>
                <VehicleRegistrationTable
                  userRole={userRole}
                  userName={userName}
                  userId={userId}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Management Tab */}
          <TabsContent value="users">
            <StaffManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
